import { inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { updatePreset } from '@primeuix/themes';
import { LayoutService } from '../../ui-components/layout/service/app.layout.service';
// import { LayoutService } from 'shared';

/** PrimeNG v21 primary palette per insurer. */
const INSURER_PRESETS: Record<string, Record<string, any>> = {
  eurolife: {
    semantic: {
      colorScheme: {
        light: {
          primary: {
            color:         '#ff303d',
            inverseColor:  '#ffffff',
            hoverColor:    '#b2212a',
            activeColor:   '#8c1520',
            contrastColor: '#ffffff',
          },
          highlight: {
            background:      'rgba(255,48,61,0.10)',
            focusBackground: 'rgba(255,48,61,0.24)',
            color:           '#ff303d',
            focusColor:      '#b2212a',
          },
          focusRing: {
            color:  '#ff303d',
            shadow: '0 0 0 2px #ffffff, 0 0 0 0.25rem #ff303d',
          },
        },
      },
    },
  },
  cov: {
    semantic: {
      colorScheme: {
        light: {
          primary: {
            color:         '#1452a8',
            inverseColor:  '#ffffff',
            hoverColor:    '#297fff',
            activeColor:   '#2170e7',
            contrastColor: '#ffffff',
          },
          highlight: {
            background:      'rgba(82,151,255,0.10)',
            focusBackground: 'rgba(82,151,255,0.24)',
            color:           '#2170e7',
            focusColor:      '#1a4db3',
          },
          focusRing: {
            color:  '#1452a8',
            shadow: '0 0 0 2px #ffffff, 0 0 0 0.25rem #1452a8',
          },
        },
      },
    },
  },
};

/** Direct component-level CSS variable overrides per insurer.
 *  Needed because PrimeNG v21 pre-computes these at init time from
 *  the preset and updatePreset() alone does not cascade to them. */
const INSURER_PALETTES: Record<string, Record<string, string>> = {
  eurolife: {
    '--p-primary-color':                    '#ff303d',
    '--p-primary-500':                      '#ff303d',
    '--p-button-primary-background':        '#485294',
    '--p-button-primary-hover-background':  '#b2212a',
    '--p-button-primary-active-background': '#8c1520',
    '--p-button-primary-border-color':      '#485294',
    '--p-button-primary-hover-border-color':'#b2212a',
    '--p-button-primary-color':             '#ffffff',
    '--p-highlight-background':             'rgba(255,48,61,0.10)',
    '--p-highlight-color':                  '#b2212a',
    '--p-focus-ring-color':                 '#ff303d',
  },
  cov: {
    '--p-primary-color':                    '#1452a8',
    '--p-primary-500':                      '#5297ff',
    '--p-button-primary-background':        '#1452a8',
    '--p-button-primary-hover-background':  '#297fff',
    '--p-button-primary-active-background': '#2170e7',
    '--p-button-primary-border-color':      '#1452a8',
    '--p-button-primary-hover-border-color':'#297fff',
    '--p-button-primary-color':             '#ffffff',
    '--p-highlight-background':             'rgba(82,151,255,0.10)',
    '--p-highlight-color':                  '#2170e7',
    '--p-focus-ring-color':                 '#1452a8',
  },
};

@Injectable({
  providedIn: 'root',
})
export class InsurerConfigService {
  userConfigs = [
    {
      insurerName: 'eurolife hellas',
      appName: 'PHI - Eurolife',
      themeColor: 'eurolife',
      logoUrl: '../../assets/layout/images/phi_eurolife.png',
    },
    {
      insurerName: 'Interamerican Hellas',
      appName: 'PHI - Insurance',
      themeColor: 'cov',
      logoUrl: '../../assets/layout/images/phidelity_logo.png',
    },
    {
      insurerName: 'Εθνική Ασφαλιστική Ελλάδος',
      appName: 'PHI - Insurance',
      themeColor: 'cov',
      logoUrl: '../../assets/layout/images/phi_easfalistiki.png',
    },
    {
      insurerName: 'Qualco User3',
      appName: 'PHI - Qualco',
      themeColor: 'cov',
      logoUrl: '../../assets/layout/images/qualco_logo.png',
    },
  ];
  private titleService = inject(Title);
  private layoutService = inject(LayoutService);
  constructor() {}

  getUserConfig(insurerName: string) {
    if (!insurerName) {
      return {
        insurerName: 'Interamerican Hellas',
        appName: 'PHI - Insurance',
        themeColor: 'cov',
        logoUrl: '../../assets/layout/images/phidelity_logo.png',
      };
    }
    let config = this.userConfigs.find(
      (insurer) =>
        insurer.insurerName.toLocaleLowerCase() ==
        insurerName.toLocaleLowerCase()
    );
    return config;
  }

  setInsurerConfig(insurer: any | undefined) {
    const insurance = this.getUserConfig(insurer?.name ?? insurer);
    this.titleService.setTitle(insurance!.appName);
    this.layoutService.logoUrl.set(insurance!.logoUrl);
    this.layoutService.config.update((config) => ({
      ...config,
      theme: insurance!.themeColor,
    }));
    // Update PrimeNG v21 semantic preset tokens (primary color etc.)
    const preset = INSURER_PRESETS[insurance!.themeColor];
    if (preset) {
      updatePreset(preset);
    }
    // Also directly update component-level CSS vars that PrimeNG pre-computes
    // at init time from the semantic tokens (updatePreset alone doesn't cascade to these).
    const palette = INSURER_PALETTES[insurance!.themeColor];
    if (palette) {
      const root = document.documentElement;
      Object.entries(palette).forEach(([key, val]) => root.style.setProperty(key, val as string));
    }
  }
}
